<template>
  <component
    :is="compoType"
    class="appearance-none select-none outline-none rounded-lg border items-center whitespace-nowrap transition-colors uppercase text-xs font-semibold"
    :class="[
      typeClass,
      stateClass,
      compoType !== 'button' ? 'inline-flex' : 'flex',
      size === 'default'
        ? 'h-10 px-5 tracking-widest'
        : 'h-8 px-3 tracking-wider',
      withIcon && '!px-0',
    ]"
    v-bind="{ ...opts, ...$attrs }"
  >
    <div class="relative" v-if="loading">
      <span class="opacity-0"><slot /></span>
      <div class="absolute spinner">
        <CoreIcon icon-id="spinner" />
      </div>
    </div>
    <slot v-else />
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

// todo: check if we can safely switch to just `inline-flex` for all button types

type ButtonType = 'primary' | 'secondary' | 'tertiary'
interface Options {
  target?: string
  to?: string
  href?: string
}

const {
  link,
  external = false,
  type = 'primary',
  disabled = false,
  loading = false,
  withIcon = false,
  size = 'default',
} = defineProps<{
  link?: string
  external?: boolean
  type?: ButtonType
  disabled?: boolean
  loading?: boolean
  withIcon?: boolean
  size?: 'default' | 'small'
}>()

const typeClass = computed(() => {
  if (type === 'primary') {
    return 'text-white hover:bg-pink-deep hover:border-pink-deep'
  }
  if (type === 'secondary') {
    return 'text-navy-deep hover:text-white hover:bg-navy-deep'
  }
  if (type === 'tertiary') {
    return 'border-pink-deep text-white hover:bg-navy-deep hover:border-navy-deep'
  }
  return ''
})

const stateClass = computed(() => {
  let extra = ''
  if (loading) {
    return `pointer-events-none`
  }

  switch (type) {
    case 'primary':
      extra = disabled ? 'bg-gray-light' : 'bg-navy-deep border-navy-deep'
      break
    case 'secondary':
      extra = disabled ? 'bg-gray-light' : 'bg-transparent border-navy-deep'
      break
    case 'tertiary':
      extra = 'bg-pink-deep'
      break
    default:
      break
  }

  if (disabled) {
    return `pointer-events-none border-gray-light ${extra}`
  } else {
    return `cursor-pointer ${extra}`
  }
})

const compoType = computed(() => {
  if (link) {
    if (link.startsWith('http')) return 'a'
    return 'router-link'
  }
  return 'button'
})

const opts = computed(() => {
  const o = {} as Options
  if (link) {
    if (link.startsWith('http')) o.href = link
    else o.to = link
  }
  if (external) o.target = '_blank'

  return o
})
</script>

<style lang="scss" scoped>
.spinner {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
